import type { PassportSection } from './informationSystemPassportTypes'

export type InformationSystemPanelContext =
  | { kind: 'overview'; systemId: string }
  | { kind: 'assets'; systemId: string }
  | { kind: 'criticalData'; systemId: string }
  | { kind: 'standards'; systemId: string }
  | { kind: 'vulnerability'; systemId: string; vulnerabilityId?: string; label?: string }
  | { kind: 'control'; systemId: string; controlLinkId?: string; label?: string }
  | { kind: 'risk'; systemId: string; sourceType?: string; sourceId?: string; label?: string }
  | { kind: 'readiness'; systemId: string }
  | { kind: 'evidence'; systemId: string }
  | { kind: 'remediation'; systemId: string }
  | { kind: 'graph'; systemId: string }
  | { kind: 'executive'; systemId: string }
  | { kind: 'fullPassport'; systemId: string; section?: PassportSection }

export type ContextNavKind = InformationSystemPanelContext['kind']

export const CONTEXT_NAV_ITEMS: ContextNavKind[] = [
  'overview',
  'assets',
  'criticalData',
  'standards',
  'vulnerability',
  'control',
  'risk',
  'readiness',
  'evidence',
  'remediation',
  'graph',
  'executive',
]

export function contextNavLabel(
  kind: ContextNavKind,
  t: (key: string, fallback?: string) => string
): string {
  const map: Record<ContextNavKind, { key: string; fallback: string }> = {
    overview: { key: 'assets.panelContext.overview', fallback: 'Overview' },
    assets: { key: 'assets.panelContext.assets', fallback: 'Assets' },
    criticalData: { key: 'assets.panelContext.criticalData', fallback: 'Critical data' },
    standards: { key: 'assets.panelContext.standards', fallback: 'Standards' },
    vulnerability: { key: 'assets.panelContext.vulnerabilities', fallback: 'Vulnerabilities' },
    control: { key: 'assets.panelContext.controls', fallback: 'Controls' },
    risk: { key: 'assets.panelContext.risks', fallback: 'Risks' },
    readiness: { key: 'assets.panelContext.readiness', fallback: 'Readiness' },
    evidence: { key: 'assets.panelContext.evidence', fallback: 'Evidence' },
    remediation: { key: 'assets.panelContext.remediation', fallback: 'Remediation' },
    graph: { key: 'assets.panelContext.graph', fallback: 'Graph' },
    executive: { key: 'enterpriseOperationalUx.executiveContext', fallback: 'Executive' },
    fullPassport: { key: 'assets.postureRow.openPassport', fallback: 'Passport' },
  }
  const entry = map[kind]
  return t(entry.key, entry.fallback)
}

export function panelContextTitle(
  ctx: InformationSystemPanelContext,
  t: (key: string, fallback?: string) => string
): string {
  if (ctx.kind === 'fullPassport') {
    return t('assets.postureRow.openPassport', 'Full passport')
  }
  if (ctx.kind === 'vulnerability' && ctx.label) {
    return t('assets.panelContext.vulnerabilityDetail', 'Top vulnerability')
  }
  if (ctx.kind === 'control' && ctx.label) {
    return t('assets.panelContext.controlDetail', 'Failed control')
  }
  if (ctx.kind === 'risk' && ctx.label) {
    return t('assets.panelContext.riskDetail', 'Open risk')
  }
  return contextNavLabel(ctx.kind, t)
}

export function panelContextFor(
  systemId: string,
  kind: ContextNavKind | 'fullPassport'
): InformationSystemPanelContext {
  if (kind === 'fullPassport') {
    return { kind: 'fullPassport', systemId, section: 'overview' }
  }
  return { kind, systemId }
}

export function switchContextKind(
  current: InformationSystemPanelContext,
  kind: ContextNavKind
): InformationSystemPanelContext {
  const systemId = current.systemId
  switch (kind) {
    case 'overview':
      return { kind: 'overview', systemId }
    case 'assets':
      return { kind: 'assets', systemId }
    case 'criticalData':
      return { kind: 'criticalData', systemId }
    case 'standards':
      return { kind: 'standards', systemId }
    case 'vulnerability':
      return { kind: 'vulnerability', systemId }
    case 'control':
      return { kind: 'control', systemId }
    case 'risk':
      return { kind: 'risk', systemId }
    case 'readiness':
      return { kind: 'readiness', systemId }
    case 'evidence':
      return { kind: 'evidence', systemId }
    case 'remediation':
      return { kind: 'remediation', systemId }
    case 'graph':
      return { kind: 'graph', systemId }
    case 'executive':
      return { kind: 'executive', systemId }
    default:
      return current
  }
}

export function drillDownToContext(target: {
  kind: 'vulnerability' | 'control' | 'risk'
  systemId: string
  id?: string
  label?: string
  sourceType?: string
  sourceId?: string
}): InformationSystemPanelContext {
  if (target.kind === 'vulnerability') {
    return {
      kind: 'vulnerability',
      systemId: target.systemId,
      vulnerabilityId: target.id,
      label: target.label,
    }
  }
  if (target.kind === 'control') {
    return {
      kind: 'control',
      systemId: target.systemId,
      controlLinkId: target.id,
      label: target.label,
    }
  }
  return {
    kind: 'risk',
    systemId: target.systemId,
    sourceType: target.sourceType,
    sourceId: target.sourceId || target.id,
    label: target.label,
  }
}
